// src/components/EducationalBackground.js
import React from 'react';
import { FaGraduationCap } from 'react-icons/fa';
import { motion } from 'framer-motion';

const EducationalBackground = ({ formData, handleChange, errors }) => {
  const getBorderClass = (field) => errors[field] ? 'is-invalid' : '';
  const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };

  // Check if user is a student
  const isStudent = formData.membershipType === 'student';

  return (
    <motion.div {...fadeIn}>
      {/* Section Header */}
      <div className="d-flex align-items-center mb-4">
        <FaGraduationCap className="me-3" style={{ fontSize: '2rem', color: '#6B8E23' }} />
        <h2 className="h3 fw-semibold mb-0" style={{ color: '#6B8E23' }}>Educational Background</h2>
      </div>

      {/* Educational Information Card */}
      <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-12">
              <label htmlFor="highestEducation" className="form-label fw-medium">
                Highest Education Level <span className="text-danger">*</span>
              </label>
              <select
                id="highestEducation"
                name="highestEducation"
                value={formData.highestEducation}
                onChange={(e) => handleChange('highestEducation', e.target.value)}
                className={`form-select ${getBorderClass('highestEducation')}`}
              >
                <option value="">Select Level</option>
                <option value="senior_high">Senior High School</option>
                <option value="diploma">Diploma</option>
                <option value="bachelor">Bachelor's Degree (BSC)</option>
                <option value="msc">Master of Science (MSc)</option>
                <option value="ma">Master of Arts (MA)</option>
                <option value="med">Master of Education (MEd)</option>
                <option value="mph">Master of Public Health (MPH)</option>
                <option value="mba">Master of Business Administration (MBA)</option>
                <option value="mres">Master of Research (MRes)</option>
                <option value="other_master">Other Master's Degree</option>
                <option value="phd">PhD/Doctorate</option>
                <option value="other">Other Professional Qualification</option>
              </select>
              {errors.highestEducation && <div className="invalid-feedback d-block">{errors.highestEducation}</div>}

              {/* Education Level Warning - Only for Student Members selecting MSC or higher */}
              {isStudent && formData.highestEducation && ['msc', 'ma', 'med', 'mph', 'mba', 'mres', 'other_master', 'phd', 'other'].includes(formData.highestEducation) && (
                <div className="alert alert-warning mt-2 mb-0" style={{ fontSize: '0.875rem' }}>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Note:</strong> Student membership is available for those pursuing Senior High School, Diploma, or Bachelor's Degree (BSC).
                  With a Master's degree or higher, you qualify for <strong>Professional Membership</strong>.
                  Please go back and select Professional Membership type.
                </div>
              )}
            </div>

            <div className="col-12">
              <label htmlFor="institution" className="form-label fw-medium">
                Institution Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="institution"
                name="institution"
                value={formData.institution}
                onChange={(e) => handleChange('institution', e.target.value)}
                className={`form-control ${getBorderClass('institution')}`}
                placeholder="e.g., University of Lagos"
              />
              {errors.institution && <div className="invalid-feedback d-block">{errors.institution}</div>}
            </div>

            {/* Graduation Year - Only for NON-STUDENTS */}
            {!isStudent && (
              <div className="col-md-6">
                <label htmlFor="graduationYear" className="form-label fw-medium">
                  Graduation Year <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  id="graduationYear"
                  name="graduationYear"
                  value={formData.graduationYear}
                  onChange={(e) => handleChange('graduationYear', e.target.value)}
                  className={`form-control ${getBorderClass('graduationYear')}`}
                  placeholder={new Date().getFullYear()}
                  min="1950"
                  max={new Date().getFullYear()}
                />
                {errors.graduationYear && <div className="invalid-feedback d-block">{errors.graduationYear}</div>}
              </div>
            )}

            <div className={isStudent ? "col-12" : "col-md-6"}>
              <label htmlFor="programAccreditation" className="form-label fw-medium">
                Program Accreditation
              </label>
              <select
                id="programAccreditation"
                name="programAccreditation"
                value={formData.programAccreditation}
                onChange={(e) => handleChange('programAccreditation', e.target.value)}
                className={`form-select ${getBorderClass('programAccreditation')}`}
              >
                <option value="">Select Status</option>
                <option value="yes">Yes, accredited</option>
                <option value="no">No, not accredited</option>
              </select>
            </div>

            {/* Expected Completion Date for Students - REQUIRED */}
            {isStudent && (
              <div className="col-12">
                <div className="alert alert-info d-flex align-items-center" style={{ backgroundColor: '#e8f5e9', borderColor: '#6B8E23' }}>
                  <i className="fas fa-info-circle me-2" style={{ color: '#6B8E23' }}></i>
                  <small>As a student member, please provide your expected date/year of completion. You will receive an email to upgrade to Professional Membership when this date arrives.</small>
                </div>
              </div>
            )}

            {isStudent && (
              <div className="col-md-6">
                <label htmlFor="expectedCompletionDate" className="form-label fw-medium">
                  Expected Completion Date/Year <span className="text-danger">*</span>
                </label>
                <input
                  type="month"
                  id="expectedCompletionDate"
                  name="expectedCompletionDate"
                  value={formData.expectedCompletionDate || ''}
                  onChange={(e) => handleChange('expectedCompletionDate', e.target.value)}
                  className={`form-control ${getBorderClass('expectedCompletionDate')}`}
                  min={new Date().toISOString().slice(0, 7)}
                  placeholder="YYYY-MM"
                />
                {errors.expectedCompletionDate && <div className="invalid-feedback d-block">{errors.expectedCompletionDate}</div>}
                <small className="text-muted">Select the month and year you expect to complete your studies</small>
              </div>
            )}

            <div className="col-12">
              <label htmlFor="certifications" className="form-label fw-medium">
                Additional Certifications <span className="text-muted fw-normal">(Optional)</span>
              </label>
              <textarea
                id="certifications"
                name="certifications"
                rows="3"
                value={formData.certifications}
                onChange={(e) => handleChange('certifications', e.target.value)}
                className={`form-control ${getBorderClass('certifications')}`}
                placeholder="List any relevant professional certifications..."
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
export default EducationalBackground;